import { useState, useEffect } from 'react';
import { isSignedIn, getSession, signOut } from '../api';

/* CSS media-query fallback — guarantees mobile layout even if JS state lags */
const mobileCSS = `
@media (max-width: 768px) {
  .nav-desktop { display: none !important; }
  .nav-mobile  { display: flex !important; }
}
@media (min-width: 769px) {
  .nav-desktop { display: flex !important; }
  .nav-mobile  { display: none !important; }
}
`;

export default function Nav({ page, navigate }) {
  const session = getSession();
  const loggedIn = isSignedIn();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= 768); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close menu on navigation
  function nav(p, slug) {
    setMenuOpen(false);
    navigate(p, slug);
  }

  const s = {
    nav: {
      borderBottom: '1px solid var(--border)',
      background: 'rgba(17,17,24,0.85)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      position: 'sticky', top: 0, zIndex: 100,
    },
    inner: {
      maxWidth: 1100, margin: '0 auto', padding: '0 16px',
      height: 56, display: 'flex', alignItems: 'center', gap: 6,
    },
    logo: {
      fontWeight: 700, fontSize: 18, cursor: 'pointer',
      letterSpacing: '-0.03em', marginRight: 16,
      background: 'linear-gradient(135deg, var(--accent2), var(--accent))',
      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    },
    link: (active) => ({
      color: active ? 'var(--text)' : 'var(--muted)',
      fontWeight: active ? 500 : 400,
      fontSize: 14, cursor: 'pointer',
      padding: '6px 12px', borderRadius: 'var(--radius-xs)',
      background: active ? 'var(--accent-glow)' : 'transparent',
      transition: 'all 0.15s ease',
    }),
    actions: { marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' },
    btnOutline: {
      padding: '6px 12px', borderRadius: 'var(--radius-xs)', fontSize: 12,
      fontWeight: 500, border: '1px solid var(--border)',
      background: 'transparent', color: 'var(--text)', cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    btnPrimary: {
      padding: '6px 14px', borderRadius: 'var(--radius-xs)', fontSize: 12,
      fontWeight: 600, border: 'none',
      background: 'linear-gradient(135deg, var(--accent), #6558d4)',
      color: '#fff', cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(124,111,239,0.3)',
      whiteSpace: 'nowrap',
    },
    dot: {
      width: 7, height: 7, borderRadius: '50%',
      background: 'var(--green)',
      boxShadow: '0 0 6px var(--green)',
      display: 'inline-block',
    },
    authInfo: {
      display: 'flex', alignItems: 'center', gap: 6,
      fontSize: 12, color: 'var(--muted)',
      padding: '4px 10px', borderRadius: 99,
      background: 'var(--green-glow)',
    },
    hamburger: {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'transparent', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-xs)', padding: '4px 8px',
      fontSize: 20, cursor: 'pointer', color: 'var(--text)',
      marginLeft: 'auto',
    },
    dropdown: {
      position: 'absolute', top: 56, left: 0, right: 0,
      background: 'rgba(17,17,24,0.97)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderBottom: '1px solid var(--border)',
      padding: '12px 16px',
      display: 'flex', flexDirection: 'column', gap: 8,
      zIndex: 99,
    },
    dropdownLink: (active) => ({
      color: active ? 'var(--text)' : 'var(--muted)',
      fontWeight: active ? 500 : 400,
      fontSize: 14, cursor: 'pointer',
      padding: '8px 12px', borderRadius: 'var(--radius-xs)',
      background: active ? 'var(--accent-glow)' : 'transparent',
    }),
  };

  function handleSignOut() {
    signOut();
    nav('marketplace');
  }

  const navLinks = (
    <>
      <span style={s.link(page === 'marketplace')} onClick={() => nav('marketplace')}>
        Explore
      </span>
      <span style={s.link(page === 'quickstart')} onClick={() => nav('quickstart')}>
        Docs
      </span>
      <span style={s.link(page === 'playground')} onClick={() => nav('playground')}>
        Playground
      </span>
      <span style={s.link(page === 'register')} onClick={() => nav('register')}>
        Publish
      </span>
    </>
  );

  const actionButtons = loggedIn ? (
    <>
      <span style={s.authInfo}>
        <span style={s.dot} /> {session?.email?.split('@')[0] || 'connected'}
      </span>
      <button style={s.btnOutline} onClick={() => nav('dashboard')}>My Builds</button>
      <button style={s.btnOutline} onClick={() => nav('consumer')}>My Account</button>
      <button style={{ ...s.btnOutline, color: 'var(--muted)', fontSize: 12, border: 'none' }} onClick={handleSignOut}>
        Sign out
      </button>
    </>
  ) : (
    <>
      <button style={s.btnOutline} onClick={() => nav('signin')}>Sign in</button>
      <button style={s.btnPrimary} onClick={() => nav('getkey')}>Get API key</button>
    </>
  );

  return (
    <nav style={s.nav}>
      <style>{mobileCSS}</style>
      <div style={s.inner}>
        <span style={s.logo} onClick={() => nav('marketplace')}>
          invokeAPI
        </span>

        {/* Desktop nav — hidden via CSS on small screens */}
        <div className="nav-desktop" style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
          {navLinks}
          <div style={s.actions}>
            {actionButtons}
          </div>
        </div>

        {/* Mobile hamburger — hidden via CSS on large screens */}
        <div className="nav-mobile" style={{ display: 'none', marginLeft: 'auto' }}>
          <button style={s.hamburger} onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? '\u2715' : '\u2630'}
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {(isMobile || window.innerWidth <= 768) && menuOpen && (
        <div style={s.dropdown}>
          <span style={s.dropdownLink(page === 'marketplace')} onClick={() => nav('marketplace')}>
            Explore
          </span>
          <span style={s.dropdownLink(page === 'quickstart')} onClick={() => nav('quickstart')}>
            Docs
          </span>
          <span style={s.dropdownLink(page === 'playground')} onClick={() => nav('playground')}>
            Playground
          </span>
          <span style={s.dropdownLink(page === 'register')} onClick={() => nav('register')}>
            Publish
          </span>
          {loggedIn ? (
            <>
              <span style={s.dropdownLink(page === 'dashboard')} onClick={() => nav('dashboard')}>
                My Builds
              </span>
              <span style={s.dropdownLink(page === 'consumer')} onClick={() => nav('consumer')}>
                My Account
              </span>
              <span style={{ ...s.dropdownLink(false), color: 'var(--muted)' }} onClick={handleSignOut}>
                Sign out
              </span>
            </>
          ) : (
            <>
              <span style={s.dropdownLink(page === 'signin')} onClick={() => nav('signin')}>
                Sign in
              </span>
              <span style={s.dropdownLink(page === 'getkey')} onClick={() => nav('getkey')}>
                Get API key
              </span>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
